// 付箋・テキストオブジェクトの「編集中も即時 WYSIWYG（マーカー除去）」を実現する共有ロジック。
//
// 方針（設計 plan.md v3）:
// - 保存は生 Markdown のまま。編集領域 DOM は「生 Markdown の 1 行 = 1 ブロック <div>」の正準構造とし、
//   記法マーカーは data 属性の隠しプレフィックス（data-prefix）へ、箇条書きグリフと番号付きリストの
//   項番は編集不可のマーカー span（.rc-md-marker）へ逃がす。本文だけをテキストノードに置くことで、
//   キャレット/本文オフセット計算を単純化する。引用は隠しプレフィックス + CSS クラス（左罫）、
//   水平線だけはプレフィックスを空にして `---` を可視本文のまま残す（記法を編集可能に保つため）。
// - data-prefix に元の生プレフィックスをそのまま保持するため build↔serialize が生テキストを
//   （LF 正規化のもとで）完全ラウンドトリップする（`*`/`+` や字下げをユーザー入力どおり保存）。
// - Enter / 削除 / インデント等の構造変更は「生テキスト + キャレット/選択の純粋関数」として実装し、
//   DOM 非依存で単体テスト可能にする。呼び出し側（use-object-text-editor.ts）は beforeinput で
//   構造変更を横取りし、純粋変換 → 正準ブロック再構築 → キャレット復元、の順で適用する。
// - innerHTML / dangerouslySetInnerHTML は使わず createElement + textContent のみで構築する
//   （ユーザー入力が HTML 解釈される余地を構造的に排除。markdown-text.ts の buildMarkdownTextContent と同方針）。

import {
  buildMarkdownSourceMap,
  displayOffsetInLine,
  displayOffsetInRange,
  markdownCalloutAtLine,
  markdownCalloutHasTitle,
  markdownCalloutTitleDisplayText,
  markdownCodeBlockAtLine,
  markdownInlineDecorationRanges,
  markdownOrderedSourceDigits,
  markdownOrderedSourceNumber,
  markdownRawLineText,
  markdownTableAtLine,
  splitRawRangeByRuns,
  NO_MARKDOWN_INLINE_DECORATION,
  ORDERED_NUMBER_MAX,
  type MarkdownCallout,
  type MarkdownCalloutLineRole,
  type MarkdownCodeBlock,
  type MarkdownCodeBlockLineRole,
  type MarkdownInlineDecoration,
  type MarkdownSourceLine,
  type MarkdownSourceMap,
  type MarkdownTable,
  type MarkdownTableCell,
  type MarkdownTableRow,
  type TextInlineStyle,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import {
  markdownLineClassName,
  markdownLineStyleFor,
  markdownListMarkerClassName,
  markdownListMarkerStyleFor,
  markdownListMarkerTextFor,
  markdownTextLineFromParts,
  parseMarkdownLineParts,
  MARKDOWN_LIST_MARKER_CLASS,
  MARKDOWN_LIST_MARKER_WIDTH_PROPERTY,
  MARKDOWN_TASK_BOX_CLASS,
  type MarkdownLineParts,
  type MarkdownLineStyleContext,
  type MarkdownTextLine,
} from "./markdown-text";
import {
  textInlineDecorationClassName,
  textInlinePieceSignature,
  textInlineStyleCss,
} from "./text-inline-style-css";

/** 箇条書きブロックに付与する class（styles.css でマーカー span のスタイルを当てる）。 */
export const EDITOR_BULLET_CLASS = "rc-md-editor-bullet";
/**
 * 箇条書きマーカー（•/◦/•）と番号付きリストの項番（`1.`）を描画する編集不可 span の class。
 * 本文（テキストノード）とは区別する。
 */
export const EDITOR_MARKER_CLASS = "rc-md-marker";
/**
 * 番号付きリストブロックへ保持する採番済み項番の data 属性名（dataset では `orderedLabel`）。
 * 項番は他行の編集で変わる（`data-prefix` が同じでも表示項番が変わり得る）ため、
 * editorNeedsRebuild の正準性判定へ含める。
 */
export const EDITOR_ORDERED_LABEL_ATTRIBUTE = "data-ordered-label";
/**
 * 部分書式（inline style run）を当てる本文 span の class。本文テキストの一部であり、
 * serialize・raw offset 算出・選択復元はいずれもこの span の中のテキストノードを本文として数える
 * （マーカー span とは対称的に、除外してはいけない）。
 */
export const EDITOR_RUN_CLASS = "rc-md-run";
/** 部分書式 span へ保持する署名（正準性判定で期待構成と文字列比較するため）。 */
const EDITOR_RUN_SIGNATURE_ATTRIBUTE = "data-md-run";

/** 生テキスト + キャレット位置（生テキスト先頭からの文字オフセット）。純粋変換の戻り値。 */
export interface EditorTextAndCaret {
  readonly text: string;
  readonly caret: number;
}

/** 生テキスト上の選択範囲（start<=end。collapsed のとき start===end）。 */
export interface EditorSelection {
  readonly start: number;
  readonly end: number;
}

/** リモートキャレット描画（canvas-remote-carets-layer）向けの表示 DOM 位置。 */
export interface DisplayCaretPosition {
  /** 行（先頭から 0 始まり）。表示 DOM の `data-md-line` に対応。 */
  readonly lineIndex: number;
  /** 表の外では行の表示テキスト上のオフセット。表のセル内ではそのセルの表示テキスト上のオフセット。 */
  readonly lineOffset: number;
  /**
   * 非編集表示で、その行が表のセルに属するときの列 index（`data-md-cell`）。
   * 表に属するすべての raw 位置で必ず定まり、表の外と編集 surface では付かない。
   */
  readonly cellIndex?: number | undefined;
}

/** 受信側 DOM の種別。editor=WYSIWYG 編集 DOM（本文のみ）、view=MarkdownTextView（グリフは実テキスト）。 */
export type CaretSurface = "editor" | "view";

//------------------------------------------------------------------------------
// DOM 構築 / シリアライズ
//------------------------------------------------------------------------------

/**
 * 生 Markdown から正準ブロック DOM を構築して `el` の子として設定する。
 * マーカーは data-prefix（隠し）へ、箇条書きグリフと番号付きリストの項番は編集不可の
 * マーカー span へ逃がし、本文だけをテキストノードに置く。空本文（空の箇条書き）は空テキストノードを
 * 常設して Range 復元先を保証する。
 *
 * 引用は data-prefix（`> `）を隠したうえで CSS クラス（左罫）を当てる。マーカー span を持たない
 * ため、本文が空の引用（`>` / `> `）は空行と同じ `<br>` だけの構造にして 1 行分の高さを保つ。
 * 水平線は data-prefix を空にし、本文（`---`）を文字として残したまま CSS クラスで横罫を重ねる
 * （マーカーを隠すと「見えない文字だけの行」になり削除・編集ができなくなるため）。
 *
 * 部分書式（inline styles）を渡した場合は、各行の可視本文を run 境界で分割し、上書きのある区間だけを
 * `span.rc-md-run` へ包む。span も本文の一部として数えるため、serialize・raw offset・選択復元は
 * いずれもマーカー span を除く子孫テキストノードの総和で動く。
 * DOM API と textContent のみで構築し、innerHTML は導入しない。
 */
export function buildMarkdownEditorDom(
  el: HTMLElement,
  rawText: string,
  inlineStyles?: readonly TextInlineStyleRange[] | undefined
): void {
  const doc = el.ownerDocument;
  el.replaceChildren();
  const sourceMap = buildMarkdownSourceMap(rawText);
  // 直前の行の種別を持ち回り、連続する箇条書きの項目間隔を非編集表示と同じ規則で付ける。
  // 行の種別が変われば editorNeedsRebuild が再構築を判定するため、間隔の付け外しも自動的に追随する。
  let previousKind: MarkdownLineParts["kind"] | undefined;
  for (const line of sourceMap.lines) {
    const div = doc.createElement("div");
    div.dataset.md = line.kind;
    // raw 行 index。リモートキャレット層が非編集表示と同じ `[data-md-line]` で行要素を引くために付ける
    //（編集 DOM は行数が変わるたびに作り直されるため、index は常に最新の行と対応する）。
    div.dataset.mdLine = String(line.lineIndex);
    if (
      line.kind === "heading" ||
      line.kind === "bullet" ||
      line.kind === "ordered" ||
      line.kind === "task"
    ) {
      div.dataset.level = String(line.level);
    }
    // 生プレフィックスをそのまま保持する（serialize での完全復元とキャレット座標系の基準）。
    div.dataset.prefix = line.rawPrefix;
    // 表示モデル（行スタイル・CSS クラス・採番済み項番の単一の真実）。
    const model = markdownTextLineFromParts(line, {
      orderedLabel: sourceMap.orderedNumbers.get(line.lineIndex),
    });
    applyLineStyle(div, model, { previousKind });
    previousKind = line.kind;
    if (line.kind === "bullet") {
      div.classList.add(EDITOR_BULLET_CLASS);
      // マーカー（•/◦/•）は編集不可の span として本文の手前に置く。本文はこの span の直後の
      // テキストノードに入るため、キャレットは常にマーカーの右（本文先頭）に来て、本文が空でも
      // マーカーへ重ならない。serialize は本文テキストノードだけを読み、この span は除外する。
      div.appendChild(createMarkerSpan(doc, model));
    }
    if (model.kind === "ordered") {
      // 番号付きリストは採番後の項番（`1-1.` など）を、箇条書きのグリフと同じ仕組みで
      // 編集不可 span として本文の手前へ置く。項番は他行の編集で変わるため data 属性にも保持し、
      // editorNeedsRebuild が「data-prefix は同じだが項番が変わった」状態を検知できるようにする。
      div.setAttribute(EDITOR_ORDERED_LABEL_ATTRIBUTE, model.label);
      div.appendChild(createMarkerSpan(doc, model));
    }
    if (model.kind === "task") {
      div.appendChild(createTaskBoxSpan(doc, model.checked));
    }
    if (line.kind === "blank" || (line.kind === "quote" && line.content.length === 0)) {
      // 空行は <br> で 1 行分の高さを維持する（空 div は高さ 0 になるため）。
      // 本文が空の引用も、マーカー span を持たないため同じ構造にする（左罫だけが 1 行分出る）。
      div.appendChild(doc.createElement("br"));
    } else {
      for (const piece of contentPiecesFor(rawText, line, inlineStyles)) {
        // 空本文でも Range を張れるよう、空文字列でもテキストノードを常設する。
        if (piece.signature === "") {
          div.appendChild(doc.createTextNode(piece.text));
          continue;
        }
        const span = doc.createElement("span");
        span.className = EDITOR_RUN_CLASS;
        span.setAttribute(EDITOR_RUN_SIGNATURE_ATTRIBUTE, piece.signature);
        applyRunStyle(span, piece);
        span.appendChild(doc.createTextNode(piece.text));
        div.appendChild(span);
      }
    }
    el.appendChild(div);
  }
}

/**
 * 正準ブロック DOM から生 Markdown を復元する。各直下ブロックの `data-prefix + textContent` を
 * `\n` で連結する（::before グリフは textContent に含まれないため本文だけが取れる）。
 * 前提: 構造を壊す入力（改行・削除・貼付・範囲置換）は呼び出し側の beforeinput 制御で横取り済みで、
 * DOM は常に正準ブロック構造であること。
 */
export function serializeMarkdownEditorDom(el: HTMLElement): string {
  const lines: string[] = [];
  // ブラウザが正準ブロックの外へ差し込んだ裸テキストノード（空エディタへの初回入力などで起こり得る）を
  // 取りこぼさないよう、childNodes を走査して直近ブロックの本文へ寄せる。次の onInput 再構築で正準化される。
  let pendingStray = "";
  for (const node of Array.from(el.childNodes)) {
    if (node instanceof HTMLElement && node.dataset.md !== undefined) {
      const prefix = trustedBlockPrefix(node);
      // 本文は直下のテキストノードのみ（マーカー span 等の要素は除外する）。
      lines.push(pendingStray + prefix + blockContentText(node));
      pendingStray = "";
    } else {
      pendingStray += node.textContent ?? "";
    }
  }
  if (pendingStray.length > 0) {
    if (lines.length > 0) lines[lines.length - 1] += pendingStray;
    else lines.push(pendingStray);
  }
  return lines.join("\n");
}

//------------------------------------------------------------------------------
// 選択 / キャレットの保存・復元（生テキストのフラットオフセット座標系）
//------------------------------------------------------------------------------

/**
 * 現在の選択範囲を生テキストオフセットで返す。フォーカスが `el` 外なら null。
 * collapsed（キャレットのみ）のときは start===end。
 */
export function saveEditorSelection(el: HTMLElement): EditorSelection | null {
  const view = el.ownerDocument.defaultView;
  const selection = view?.getSelection?.() ?? null;
  if (!selection || selection.rangeCount === 0) return null;
  return rawSelectionFromRange(el, selection.getRangeAt(0));
}

/** collapsed キャレットの生テキストオフセットを返す（saveEditorSelection の start 相当）。 */
export function saveEditorCaretOffset(el: HTMLElement): number | null {
  return saveEditorSelection(el)?.start ?? null;
}

/**
 * Range（beforeinput の getTargetRanges 由来の StaticRange 等）を生テキスト選択範囲へ変換する。
 * オートコレクト（insertReplacementText）のように「見た目の Selection は collapsed でも実際の対象は語」の
 * ケースがあるため、変換対象は Selection ではなく対象 Range から求める必要がある。
 */
export function rawSelectionFromRange(
  el: HTMLElement,
  range: { startContainer: Node; startOffset: number; endContainer: Node; endOffset: number }
): EditorSelection | null {
  if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return null;
  const start = rawOffsetOfPoint(el, range.startContainer, range.startOffset);
  const end = rawOffsetOfPoint(el, range.endContainer, range.endOffset);
  if (start === null || end === null) return null;
  return start <= end ? { end, start } : { end: start, start: end };
}

/**
 * 生テキスト `raw` に対して現在の DOM（正準ブロック）を作り直す必要があるかを返す。
 * 行数・各行の種別（data-md）・生プレフィックス（data-prefix）・番号付きリストの項番
 * （data-ordered-label）のいずれかが変われば再構築が必要。
 * 本文内の通常タイプ（種別・プレフィックス不変）では false を返し、無駄な再構築とキャレット揺れを避ける。
 */
export function editorNeedsRebuild(
  el: HTMLElement,
  raw: string,
  inlineStyles?: readonly TextInlineStyleRange[] | undefined
): boolean {
  // 正準ブロック（data-md 付き）以外の子（裸テキスト・ブラウザ挿入の要素）があれば必ず作り直す。
  for (const node of Array.from(el.childNodes)) {
    if (!(node instanceof HTMLElement) || node.dataset.md === undefined) return true;
  }
  const sourceMap = buildMarkdownSourceMap(raw);
  const blocks = Array.from(el.children).filter((c): c is HTMLElement => c instanceof HTMLElement);
  if (sourceMap.lines.length !== blocks.length) return true;
  for (const [i, line] of sourceMap.lines.entries()) {
    const block = blocks[i];
    if (!block) return true;
    if (block.dataset.md !== line.kind) return true;
    if ((block.dataset.prefix ?? "") !== line.rawPrefix) return true;
    // bullet・ordered はマーカー span を必ず持つのが正準（ネイティブのブロック分割クローンは span を欠く）。
    // serialize 出力の変化からの間接検知だけだと、本文が記法で始まるケースで kind/prefix が
    // 偶然一致して非正準状態が恒久化するため、span の欠落を直接検知して再構築する。
    if (
      (line.kind === "bullet" || line.kind === "ordered" || line.kind === "task") &&
      block.querySelector(`.${EDITOR_MARKER_CLASS}`) === null
    ) {
      return true;
    }
    // 番号付きリストの項番は他行の編集で変わる（先頭へ行を挿入すると `1.` → `2.`）。
    // data-prefix が同じでも表示項番が変われば作り直す。
    if (
      line.kind === "ordered" &&
      block.getAttribute(EDITOR_ORDERED_LABEL_ATTRIBUTE) !== sourceMap.orderedNumbers.get(i)
    ) {
      return true;
    }
    // アンカーの健全性: blank と本文が空の引用は <br> を必ず持つ（ネイティブ削除で <br> が消えた
    // 場合に正準へ作り直せるよう検出する）。
    if (line.kind === "blank" || (line.kind === "quote" && line.content.length === 0)) {
      if (block.querySelector("br") === null) return true;
      continue;
    }
    // 本文は「run 境界で分割した期待構成」と完全一致するのが正準。上書きが無ければ単一テキスト
    // ノード、上書きがあれば素テキストと span.rc-md-run が期待どおり交互に並ぶ。
    // IME 確定直後はブラウザが確定テキストを別ノードとして挿入し複数テキストノード（空ノード +
    // 確定ノード）になり得るため、この一致判定で検知して正準へ作り直す（キャレットは
    // rawOffsetOfPoint が全ノード連結基準で正しく保存→復元できる）。
    if (!matchesExpectedContentNodes(block, contentPiecesFor(raw, line, inlineStyles))) {
      return true;
    }
  }
  return false;
}

/**
 * 生テキストオフセット `offset` の位置へ collapsed キャレットを復元する。
 * 該当ブロックを走査で特定し、本文オフセット = clamp(offset − 行頭 − prefix長, 0, 本文長) の位置へ Range を張る。
 * 隠しプレフィックス領域に落ちた場合は本文先頭へクランプする。
 * 本文は「マーカー span を除く子孫テキストノードの総和」として数えるため、部分書式の span で
 * 複数ノードへ分かれていても復元先を取り違えない（保存側 rawOffsetOfPoint と同じ数え方）。
 */
export function restoreEditorCaretOffset(el: HTMLElement, offset: number): void {
  restoreEditorSelection(el, offset, offset);
}

/**
 * 生テキストオフセットの範囲 `[start, end)` へ選択を復元する。
 * 範囲書式の適用でエディタ DOM を作り直した後も、同じ文字範囲を選び直さずに続けて操作できるようにする。
 * `start === end` のときは collapsed キャレットの復元（restoreEditorCaretOffset）と同じ動作になる。
 */
export function restoreEditorSelection(el: HTMLElement, start: number, end: number): void {
  const doc = el.ownerDocument;
  const view = doc.defaultView;
  const selection = view?.getSelection?.() ?? null;
  if (!selection) return;
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  const startAnchor = resolveEditorAnchor(el, lo);
  if (!startAnchor) return;
  const endAnchor = hi === lo ? startAnchor : (resolveEditorAnchor(el, hi) ?? startAnchor);
  const range = doc.createRange();
  range.setStart(startAnchor.node, startAnchor.offset);
  try {
    range.setEnd(endAnchor.node, endAnchor.offset);
  } catch {
    // 終端が始端より前になる想定外の構造では collapsed へ倒す（編集不能に陥らせない）。
    range.collapse(true);
  }
  selection.removeAllRanges();
  selection.addRange(range);
}

//------------------------------------------------------------------------------
// 純粋変換（生テキスト + 選択 → 生テキスト + キャレット）
//------------------------------------------------------------------------------

/**
 * `[start, end)` を `insert`（CRLF は LF 正規化）で置換する。貼り付け・ドロップ・選択上書き入力に使う。
 * キャレットは挿入後の末尾。
 */
export function insertTextAt(
  text: string,
  start: number,
  end: number,
  insert: string
): EditorTextAndCaret {
  const [lo, hi] = orderedBounds(text, start, end);
  const normalized = insert.replace(/\r\n?/g, "\n");
  return { caret: lo + normalized.length, text: text.slice(0, lo) + normalized + text.slice(hi) };
}

/**
 * Enter（改行）。要望②のリスト継続:
 * - 本文のある箇条書き行 → 同じ生プレフィックスを複製して次行も箇条書きにする（`*`/`+`・字下げを保存）。
 * - 本文のある番号付きリスト行 → 同じ字下げ + 「行に書かれた数字 + 1」+ `. ` で継続する
 *   （採番結果ではなく生テキストの数字を使う。9 桁の上限では桁上がりさせず同じ数字を挿入する）。
 * - 本文のある引用行 → 改行 + `> ` で引用を継続する。
 * - 空の箇条書き・番号付き・引用行 → マーカーを外して plain 空行にする（リスト終了・行は増やさない）。
 * - タスク行 → 従来の箇条書きとして次行へマーカーだけを継続する（タスク挿入支援は別機能）。
 * - それ以外（見出し/plain/水平線） → 通常改行を挿入（見出しを分割した後半は plain）。
 * 範囲選択があれば先に削除してから判定する。
 */
export function splitForEnter(text: string, start: number, end: number): EditorTextAndCaret {
  const [lo, hi] = orderedBounds(text, start, end);
  const base = text.slice(0, lo) + text.slice(hi);
  const pos = lo;
  const { lineStart, lineEnd, lineText } = lineBoundsAt(base, pos);
  const parts = parseMarkdownLineParts(lineText);
  const continuation = continuationPrefixFor(parts);
  if (continuation !== null) {
    if (parts.content.length === 0 && parts.kind !== "task") {
      // 空の箇条書き・番号付き・引用 → マーカー除去で plain 空行化（行頭へ）。
      return {
        caret: lineStart,
        text: base.slice(0, lineStart) + base.slice(lineEnd),
      };
    }
    // 本文あり → 改行 + 継続用プレフィックスで同じ記法を継続。
    const inserted = `\n${continuation}`;
    return { caret: pos + inserted.length, text: base.slice(0, pos) + inserted + base.slice(pos) };
  }
  // 見出し/plain/blank/水平線は通常改行。
  return { caret: pos + 1, text: `${base.slice(0, pos)}\n${base.slice(pos)}` };
}

/** Shift+Enter。常に plain 改行を挿入する（リスト継続・終了はしない）。 */
export function insertPlainLineBreak(text: string, start: number, end: number): EditorTextAndCaret {
  return insertTextAt(text, start, end, "\n");
}

/**
 * Tab / Shift+Tab によるリスト（箇条書き・番号付き・タスクリスト）のネスト変更（delta = +1 / -1）。
 * キャレット行がリスト行でなければ null（呼び出し側は preventDefault のみで no-op。引用・水平線も null）。
 * 選択が複数行にまたがる場合も開始位置（start）の行のみを対象にする（複数行一括インデントは非対応）。
 * 番号付きリストでは、行に書かれている数字をそのまま保つ（表示項番は採番の後段が決めるため、
 * 生テキストの数字は利用者の入力を尊重する）。
 */
export function changeListIndent(
  text: string,
  start: number,
  _end: number,
  delta: number,
  maxLevel: number
): EditorTextAndCaret | null {
  const { lineStart, lineEnd, lineText } = lineBoundsAt(text, start);
  const parts = parseMarkdownLineParts(lineText);
  if (parts.kind !== "bullet" && parts.kind !== "ordered" && parts.kind !== "task") {
    return null;
  }
  const newLevel = clamp(parts.level + delta, 0, maxLevel);
  // 箇条書き・タスクは生のマーカー、番号付きは「数字 + ピリオド」を保つ。
  // 数字は数値化せず文字列のまま使い、`007.` のような先頭ゼロも利用者の入力どおり残す。
  const marker =
    parts.kind === "ordered"
      ? `${markdownOrderedSourceDigits(parts) ?? "1"}. `
      : parts.rawPrefix.trimStart();
  const newPrefix = `${"  ".repeat(newLevel)}${marker}`;
  const newLine = newPrefix + parts.content;
  const newText = text.slice(0, lineStart) + newLine + text.slice(lineEnd);
  // キャレットは本文内の同じ位置に保つ（prefix 長の増減ぶんシフト）。
  const contentOffset = Math.max(0, start - lineStart - parts.rawPrefix.length);
  return { caret: lineStart + newPrefix.length + contentOffset, text: newText };
}

/**
 * Backspace の構造変換。呼び出し側は「本文先頭（境界）でのみ」本関数を使う（境界判定は isBackspaceBoundary）。
 * - 箇条書き・番号付き・タスクリスト本文先頭: level>0 は 1 段浅く、level==0 は plain（マーカー除去）。
 * - 見出し・引用本文先頭: plain（`#` / `> ` 除去）。
 * - plain/blank 行頭（前行あり）: 前行と結合。
 * 境界以外では単純な 1 文字削除（安全側のフォールバック）。
 * 隠れたマーカー（data-prefix）をネイティブ削除に委ねると、`> ` が `>` になって行が黙って
 * plain へ変わるため、新しい種別もここで構造変換として扱う。
 */
export function deleteBackward(text: string, caret: number, maxLevel: number): EditorTextAndCaret {
  if (caret <= 0) return { caret: 0, text };
  const { lineStart, lineEnd, lineText } = lineBoundsAt(text, caret);
  const parts = parseMarkdownLineParts(lineText);
  const contentStart = lineStart + parts.rawPrefix.length;
  if (
    caret === contentStart &&
    (parts.kind === "bullet" || parts.kind === "ordered" || parts.kind === "task")
  ) {
    if (parts.level > 0) {
      const outdented = changeListIndent(text, caret, caret, -1, maxLevel);
      if (outdented) return outdented;
    }
    // level 0 → plain 化（マーカー除去）。
    return {
      caret: lineStart,
      text: text.slice(0, lineStart) + parts.content + text.slice(lineEnd),
    };
  }
  if (caret === contentStart && (parts.kind === "heading" || parts.kind === "quote")) {
    return {
      caret: lineStart,
      text: text.slice(0, lineStart) + parts.content + text.slice(lineEnd),
    };
  }
  if (caret === lineStart && lineStart > 0) {
    // 行頭で前行と結合。吸収される側（本行）の生プレフィックスは落とし、本文だけを前行末尾へ繋ぐ
    // （マーカー文字が本文へ漏れないようにする）。
    const bounds = lineBoundsAt(text, caret);
    const parts = parseMarkdownLineParts(bounds.lineText);
    const prevEnd = lineStart - 1; // 前行末尾（"\n" の位置）
    return {
      caret: prevEnd,
      text: text.slice(0, prevEnd) + parts.content + text.slice(bounds.lineEnd),
    };
  }
  // フォールバック: 本文内 1 文字削除。
  return { caret: caret - 1, text: text.slice(0, caret - 1) + text.slice(caret) };
}

/**
 * Delete（前方削除）の構造変換。呼び出し側は「行末（境界）でのみ」使う（判定は isDeleteForwardBoundary）。
 * 行末で次行があれば結合、それ以外は 1 文字前方削除（フォールバック）。
 */
export function deleteForward(text: string, caret: number): EditorTextAndCaret {
  if (caret >= text.length) return { caret: text.length, text };
  const { lineEnd } = lineBoundsAt(text, caret);
  if (caret === lineEnd && lineEnd < text.length) {
    // 行末で次行と結合。吸収される次行の生プレフィックス（マーカー）は落とし、本文だけを本行末尾へ繋ぐ
    // （`- a` の行末で Delete したとき次行 `- b` の "- " が本文へ漏れて "- a- b" になるのを防ぐ）。
    const nextBounds = lineBoundsAt(text, lineEnd + 1);
    const nextParts = parseMarkdownLineParts(nextBounds.lineText);
    return {
      caret,
      text: text.slice(0, lineEnd) + nextParts.content + text.slice(nextBounds.lineEnd),
    };
  }
  return { caret, text: text.slice(0, caret) + text.slice(caret + 1) };
}

/** 範囲削除（範囲・複数ブロックの削除、カット）。`[start, end)` を削除し、キャレットは start。 */
export function deleteRange(text: string, start: number, end: number): EditorTextAndCaret {
  const [lo, hi] = orderedBounds(text, start, end);
  return { caret: lo, text: text.slice(0, lo) + text.slice(hi) };
}

/**
 * Backspace を構造変換として横取りすべきか（＝本文先頭などの境界か）。
 * 本文内での通常削除・単語削除はネイティブ委任するため false を返す。
 */
export function isBackspaceBoundary(text: string, caret: number): boolean {
  if (caret <= 0) return false;
  const { lineStart, lineText } = lineBoundsAt(text, caret);
  const parts = parseMarkdownLineParts(lineText);
  const contentStart = lineStart + parts.rawPrefix.length;
  // 隠しプレフィックスを持つ種別（bullet・task・heading・quote・ordered）は本文先頭を境界にする。
  // rule はプレフィックスが空（`---` が可視本文）のため、この判定には該当しない。
  if (
    caret === contentStart &&
    (parts.kind === "bullet" ||
      parts.kind === "heading" ||
      parts.kind === "quote" ||
      parts.kind === "ordered" ||
      parts.kind === "task")
  ) {
    return true;
  }
  // plain/blank 行頭（前行あり）での結合も横取り対象。
  return caret === lineStart && lineStart > 0;
}

/** Delete を構造変換として横取りすべきか（＝行末で次行があるか）。 */
export function isDeleteForwardBoundary(text: string, caret: number): boolean {
  if (caret >= text.length) return false;
  const { lineEnd } = lineBoundsAt(text, caret);
  return caret === lineEnd && lineEnd < text.length;
}

//------------------------------------------------------------------------------
// リモートキャレット: 生オフセット → 表示 DOM 位置（canvas-remote-carets-layer 用）
//------------------------------------------------------------------------------

/**
 * 生テキストオフセット `n` を、表示 DOM 上の { 行 index, 行内オフセット } へ写像する。
 * 表示 DOM は行ごとの <div> で改行テキストノードを持たないため、行と行内位置を分けて返す。
 * - surface="editor": 編集中 WYSIWYG DOM（本文のみ・グリフと項番はマーカー span）。行内オフセット = 本文オフセット。
 *   表の行も編集中は表を組まず生の行のままなので、1 行 1 ブロックの写像になる。
 * - surface="view": MarkdownTextView（markdownLineTextFor の実テキスト）。箇条書き本文ありは「グリフ + 空白」分、
 *   番号付きリスト本文ありは「採番済みの項番 + 空白」分を加算、空箇条書き・空番号付きはグリフ / 項番のみ
 *   （空白なし）、見出し/plain/引用は本文のみ。水平線は表示テキストが空のため常に 0。
 *   表の行はセルごとに要素が分かれるため、`cellIndex` と「そのセルの表示テキスト上のオフセット」を返す
 *   （tableCaretPosition）。コードブロックの行は raw のまま描くため、行分類にもとづく換算を通さない
 *   （codeCaretPosition）。
 */
export function mapRawOffsetToDisplay(
  text: string,
  n: number,
  surface: CaretSurface
): DisplayCaretPosition {
  // 非編集表示の換算は表示セグメント（rawOffsets）を単一の真実とするため、行内相対ではなく
  // 本文全体の絶対 offset を扱う source map から行を引く（番号付きリストの採番済み項番もここから得る）。
  const sourceMap = buildMarkdownSourceMap(text);
  for (const [i, line] of sourceMap.lines.entries()) {
    const isLast = i === sourceMap.lines.length - 1;
    if (n <= line.contentEnd || isLast) {
      if (surface === "view") {
        const code = markdownCodeBlockAtLine(sourceMap, i);
        if (code !== undefined) return codeCaretPosition(sourceMap, code.code, code.role, i, n);
        const callout = markdownCalloutAtLine(sourceMap, i);
        // 本文行は callout の外と同じ写像のため、フェンス行だけを横取りする。
        if (callout !== undefined && callout.role !== "body") {
          return calloutCaretPosition(text, sourceMap, callout.callout, callout.role, n);
        }
        const hit = markdownTableAtLine(sourceMap, i);
        if (hit !== undefined) return tableCaretPosition(text, hit.table, hit.row, n);
      }
      const contentOffset = clamp(n - line.contentStart, 0, line.content.length);
      const lineOffset =
        surface === "view"
          ? viewLineOffset(
              line,
              sourceMap.orderedNumbers.get(line.lineIndex),
              displayOffsetInLine(line, line.contentStart + contentOffset)
            )
          : contentOffset;
      return { lineIndex: i, lineOffset };
    }
  }
  return { lineIndex: 0, lineOffset: 0 };
}

/**
 * `el` 内で「表示テキスト先頭から offset 文字目」に対応する位置（ノードとノード内オフセット）を
 * 返す（リモートキャレット計測 canvas-remote-carets-layer が collapsed Range を張る位置の解決に使う）。
 * 編集中 surface の箇条書きは編集不可マーカー span（`.rc-md-marker`, `•`）を実 DOM に持つが、
 * これは表示専用で mapRawOffsetToDisplay の返す行内オフセットには含めないため、走査から除外する。
 * 非編集 surface（MarkdownTextView）もマーカー span（`.canvas-md-list-marker`）を持つが、そちらは
 * 逆に行内オフセットへ含める（viewLineOffset がマーカー文字列の長さを接頭辞として足す）ため、
 * 除外しない。除外条件を編集用クラス（`.rc-md-marker`）だけに限定しているのはこのためで、
 * 共有クラスで除外すると非編集 surface のキャレットがマーカー 1 文字ぶん右へずれる。
 *
 * **マーカー文字列をちょうど読み終えた位置（＝本文の先頭）は、次のテキストノードの先頭を返す。**
 * 非編集表示のマーカー span は固定幅（`min-width`）で中身を中央へ寄せるため、「マーカーの
 * テキストノードの末尾」は**グリフの右端**であり、本文が始まるマーカー領域の右端とは
 * `(領域の幅 − グリフの実描画幅) / 2` だけ離れている。そのまま返すと、リモートキャレットと
 * 選択帯の始端がその差ぶん左（グリフ寄り）へずれる。
 *
 * 本文が空のリスト行（マーカーだけの行）には次のテキストノードが無いため、従来どおり
 * マーカーのテキストノードの末尾を返す。**マーカー span の直後の親ノード境界（要素境界）は
 * 使えない。** collapsed Range をそこへ張ると、Chromium は行ボックスの左端（マーカー領域の
 * 左端）の矩形を返し、かえって大きくずれる。本文の無い行ではキャレットが指す文字も無いため、
 * ずれは最大でも `(領域の幅 − グリフの実描画幅) / 2` に収まる。
 *
 * テキストノードが 1 つも無い場合は null を返す（呼び出し側は要素先頭へフォールバックする）。
 */
export function resolveCaretTextAnchor(
  el: HTMLElement,
  offset: number
): { node: Text; offset: number } | null {
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.closest(`.${EDITOR_MARKER_CLASS}`)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  let node = walker.nextNode() as Text | null;
  if (!node) return null;
  let remaining = Math.max(0, offset);
  for (;;) {
    const length = node.textContent?.length ?? 0;
    if (remaining < length) return { node, offset: remaining };
    if (remaining === length) {
      // マーカーをちょうど読み終えた位置は本文の先頭。次のテキストノードの先頭へ送る。
      if (isInsideListMarker(node)) {
        const next = walker.nextNode() as Text | null;
        if (next) return { node: next, offset: 0 };
      }
      return { node, offset: remaining };
    }
    remaining -= length;
    const next = walker.nextNode() as Text | null;
    if (!next) return { node, offset: length };
    node = next;
  }
}

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

/** 非編集表示のマーカー span（`.canvas-md-list-marker`）の中のテキストノードか。 */
function isInsideListMarker(node: Text): boolean {
  return node.parentElement?.closest(`.${MARKDOWN_LIST_MARKER_CLASS}`) != null;
}

/**
 * 非編集表示のコードブロックの中へ落ちた raw offset の写像。
 *
 * 中身の行は raw のまま描くため、行分類（`bullet` など）にもとづく縮約・接頭辞加算を通してはいけない
 * （`- item` をコードブロックへ入れると、通常の換算ではグリフ分が足されて 2 文字ずれる）。
 * - 中身の行: 行頭からの raw offset をそのまま行内オフセットにする。
 * - 開始フェンス行: 最初の中身行の先頭へ寄せる（フェンス行は表示 DOM を持たない）。
 * - 終了フェンス行: 最後の中身行の末尾へ寄せる。
 * - 中身が 0 行のブロック: 開始フェンス行の index を持つ空行 span の先頭（オフセット 0）。
 */
function codeCaretPosition(
  map: MarkdownSourceMap,
  code: MarkdownCodeBlock,
  role: MarkdownCodeBlockLineRole,
  lineIndex: number,
  n: number
): DisplayCaretPosition {
  if (role === "content") {
    const rawStart = map.lines[lineIndex]?.rawStart ?? 0;
    return {
      lineIndex,
      lineOffset: clamp(n - rawStart, 0, markdownRawLineText(map, lineIndex).length),
    };
  }
  const firstContent = code.contentLineIndexes[0];
  const lastContent = code.contentLineIndexes[code.contentLineIndexes.length - 1];
  if (firstContent === undefined || lastContent === undefined) {
    // 中身 0 行のブロックは、`<br>` だけの空行 span（開始フェンス行の index）しか持たない。
    return { lineIndex: code.fenceStartLineIndex, lineOffset: 0 };
  }
  if (role === "fence-start") return { lineIndex: firstContent, lineOffset: 0 };
  return { lineIndex: lastContent, lineOffset: markdownRawLineText(map, lastContent).length };
}

/**
 * 非編集表示の callout のフェンス行へ落ちた raw offset の写像（本文行は callout の外と同じ）。
 *
 * フェンス行そのものは表示 DOM を持たないため、次のいずれかへ寄せる。
 * - 開始フェンス行: タイトルがあればタイトル要素（`data-md-line` は開始フェンス行の index）。
 *   タイトル範囲より前は先頭、範囲内はその範囲の表示オフセット、範囲より後ろ（末尾の空白）は末尾。
 *   タイトルが無ければ最初の本文行の先頭、本文も無ければ空行要素（開始フェンス行の index）の先頭。
 * - 終了フェンス行: 最後の本文行の末尾。本文 0 行ならタイトル要素の末尾、
 *   タイトルも無ければ空行要素の先頭。
 *
 * 開始フェンス行の offset を 0 から行末まで動かしても写像先は後戻りしない（後戻りすると
 * リモートの選択範囲が反転する）。範囲外を範囲の端へ丸めてから換算することで単調性を保つ。
 */
function calloutCaretPosition(
  text: string,
  map: MarkdownSourceMap,
  callout: MarkdownCallout,
  role: MarkdownCalloutLineRole,
  n: number
): DisplayCaretPosition {
  const hasTitle = markdownCalloutHasTitle(text, callout);
  const firstBody = callout.body[0];
  const lastBody = callout.body[callout.body.length - 1];
  if (role === "open") {
    if (hasTitle) {
      return {
        lineIndex: callout.openLineIndex,
        lineOffset: displayOffsetInRange(
          text,
          callout.titleStart,
          callout.titleEnd,
          clamp(n, callout.titleStart, callout.titleEnd)
        ),
      };
    }
    if (firstBody !== undefined) return { lineIndex: firstBody.lineIndex, lineOffset: 0 };
    return { lineIndex: callout.openLineIndex, lineOffset: 0 };
  }
  if (lastBody !== undefined) {
    return {
      lineIndex: lastBody.lineIndex,
      lineOffset: viewLineEndOffset(map, lastBody.lineIndex),
    };
  }
  if (hasTitle) {
    return {
      lineIndex: callout.openLineIndex,
      lineOffset: markdownCalloutTitleDisplayText(text, callout).length,
    };
  }
  return { lineIndex: callout.openLineIndex, lineOffset: 0 };
}

/** 非編集表示における、その行の表示テキストの末尾オフセット（グリフ・項番の接頭辞を含む）。 */
function viewLineEndOffset(map: MarkdownSourceMap, lineIndex: number): number {
  const line = map.lines[lineIndex];
  if (line === undefined) return 0;
  return viewLineOffset(
    line,
    map.orderedNumbers.get(line.lineIndex),
    displayOffsetInLine(line, line.contentEnd)
  );
}

/**
 * 非編集表示の表の中へ落ちた raw offset の写像。表に属するすべての raw 位置で `cellIndex` が必ず定まる。
 * - セル範囲の内側: そのセルを基準にした表示オフセット（`\|` の `\` やリンク記法など表示を持たない
 *   raw 位置は直後の表示文字へ寄る）。
 * - セル範囲の外（パイプ・区切りの空白）: 直後のセルの先頭。
 * - 行末・切り捨てた余剰セル: その行の最後の実セル（合成セルではない最後の表示セル）の末尾。
 * - 区切り行（表示 DOM を持たない）: ヘッダ行の最終セルの末尾。
 */
function tableCaretPosition(
  text: string,
  table: MarkdownTable,
  row: MarkdownTableRow | undefined,
  n: number
): DisplayCaretPosition {
  if (row === undefined) {
    // 区切り行。ヘッダ行は合成セルを持たないため、最終列がそのまま最後の実セルになる。
    const cellIndex = table.columnCount - 1;
    const cell = table.header.cells[cellIndex]!;
    return {
      cellIndex,
      lineIndex: table.headerLineIndex,
      lineOffset: cellDisplayOffset(text, cell, cell.end),
    };
  }
  let lastReal: { readonly cellIndex: number; readonly cell: MarkdownTableCell } | undefined;
  for (const [cellIndex, cell] of row.cells.entries()) {
    // 合成セルはゼロ幅で raw 範囲を持たないため、寄せ先にしない（行末は最後の実セルへ寄せる）。
    if (cell.synthesized === true) break;
    lastReal = { cell, cellIndex };
    if (n <= cell.end) {
      return {
        cellIndex,
        lineIndex: row.lineIndex,
        lineOffset: n < cell.start ? 0 : cellDisplayOffset(text, cell, n),
      };
    }
  }
  // 各行は 1 つ以上の実セルを持つ（パイプだけの行でも空セルが 1 つできる）。
  const tail = lastReal ?? { cell: row.cells[0]!, cellIndex: 0 };
  return {
    cellIndex: tail.cellIndex,
    lineIndex: row.lineIndex,
    lineOffset: cellDisplayOffset(text, tail.cell, tail.cell.end),
  };
}

/** セルの表示テキスト上のオフセット（`\|` を `|` へ畳む表のセル専用の規則で数える）。 */
function cellDisplayOffset(text: string, cell: MarkdownTableCell, rawOffset: number): number {
  return displayOffsetInRange(text, cell.start, cell.end, rawOffset, { collapsePipeEscape: true });
}

/**
 * 1 行の可視本文を「run 境界 ∪ 装飾境界」で分割した結果
 * （署名が空の片は上書きも装飾も無い＝素のテキストノード）。
 */
interface EditorContentPiece {
  readonly text: string;
  readonly signature: string;
  readonly className?: string | undefined;
  readonly color?: string | undefined;
  readonly backgroundColor?: string | undefined;
  readonly fontSize?: string | undefined;
  readonly fontWeight?: string | undefined;
  readonly fontStyle?: string | undefined;
  readonly textDecorationLine?: string | undefined;
}

/**
 * 編集 DOM の span 分割を求める。
 *
 * 装飾範囲は**マーカーを含む**（`**重要**` は 6 文字が 1 つの片）。編集中は保存本文と
 * 編集 DOM のテキストが 1 文字も食い違わないため、キャレット位置・選択範囲・Undo / Redo・
 * リモートキャレットの座標系は変わらない。
 */
function contentPiecesFor(
  rawText: string,
  line: MarkdownSourceLine,
  inlineStyles: readonly TextInlineStyleRange[] | undefined
): readonly EditorContentPiece[] {
  const headingLine = line.kind === "heading";
  const runPieces =
    inlineStyles === undefined || inlineStyles.length === 0
      ? [{ rawEnd: line.contentEnd, rawStart: line.contentStart, style: {} as TextInlineStyle }]
      : splitRawRangeByRuns({
          rawEnd: line.contentEnd,
          rawStart: line.contentStart,
          runs: inlineStyles,
          text: rawText,
        });
  const decorationRanges = markdownInlineDecorationRanges(line);
  const pieces: EditorContentPiece[] = [];
  // 装飾範囲と run 範囲はどちらも start 昇順・重なり無しで可視本文を覆うため、片方の走査位置を
  // 持ち回れば 2 つの境界集合の和で 1 回走査した分割になる（毎回全範囲を見直さない）。
  let rangeCursor = 0;
  for (const runPiece of runPieces) {
    while (
      rangeCursor < decorationRanges.length &&
      decorationRanges[rangeCursor]!.end <= runPiece.rawStart
    ) {
      rangeCursor += 1;
    }
    for (let index = rangeCursor; index < decorationRanges.length; index += 1) {
      const range = decorationRanges[index]!;
      if (range.start >= runPiece.rawEnd) break;
      const start = Math.max(runPiece.rawStart, range.start);
      const end = Math.min(runPiece.rawEnd, range.end);
      if (end <= start) continue;
      pieces.push(
        editorContentPieceOf(
          rawText.slice(start, end),
          runPiece.style,
          range.decoration,
          headingLine
        )
      );
    }
    if (decorationRanges.length === 0) {
      // 可視本文が空の行（空の箇条書きなど）。Range を張る先を保証するため空の片を残す。
      pieces.push(
        editorContentPieceOf("", runPiece.style, NO_MARKDOWN_INLINE_DECORATION, headingLine)
      );
    }
  }
  return pieces;
}

function editorContentPieceOf(
  text: string,
  style: TextInlineStyle,
  decoration: MarkdownInlineDecoration,
  headingLine: boolean
): EditorContentPiece {
  const className = textInlineDecorationClassName(decoration);
  return {
    signature: textInlinePieceSignature(style, decoration),
    text,
    ...(className !== undefined ? { className } : {}),
    ...textInlineStyleCss(style, { decoration, headingLine }),
  };
}

function applyRunStyle(span: HTMLElement, piece: EditorContentPiece): void {
  if (piece.className !== undefined) span.classList.add(...piece.className.split(" "));
  if (piece.color !== undefined) span.style.color = piece.color;
  if (piece.backgroundColor !== undefined) span.style.backgroundColor = piece.backgroundColor;
  if (piece.fontSize !== undefined) span.style.fontSize = piece.fontSize;
  if (piece.fontWeight !== undefined) span.style.fontWeight = piece.fontWeight;
  if (piece.fontStyle !== undefined) span.style.fontStyle = piece.fontStyle;
  if (piece.textDecorationLine !== undefined) {
    span.style.textDecorationLine = piece.textDecorationLine;
  }
}

/** ブロックの本文ノード列が、期待する run 分割と完全一致するか。 */
function matchesExpectedContentNodes(
  block: HTMLElement,
  expected: readonly EditorContentPiece[]
): boolean {
  const actual = Array.from(block.childNodes).filter((node) => !isMarkerElement(node));
  if (actual.length !== expected.length) return false;
  for (const [index, piece] of expected.entries()) {
    const node = actual[index];
    if (!node) return false;
    if (piece.signature === "") {
      if (node.nodeType !== Node.TEXT_NODE) return false;
      if ((node.textContent ?? "") !== piece.text) return false;
      continue;
    }
    if (!(node instanceof HTMLElement)) return false;
    if (node.getAttribute(EDITOR_RUN_SIGNATURE_ATTRIBUTE) !== piece.signature) return false;
    if ((node.textContent ?? "") !== piece.text) return false;
    // span 内も単一テキストノードが正準（IME・ネイティブ入力で分割された状態を作り直す）。
    if (node.childNodes.length !== 1 || node.firstChild?.nodeType !== Node.TEXT_NODE) return false;
  }
  return true;
}

/** 生テキストオフセットに対応する DOM ノードとノード内オフセットを返す。 */
function resolveEditorAnchor(
  el: HTMLElement,
  offset: number
): { node: Node; offset: number } | null {
  const blocks = Array.from(el.children).filter((c): c is HTMLElement => c instanceof HTMLElement);
  let base = 0;
  for (let i = 0; i < blocks.length; i += 1) {
    const block = blocks[i];
    if (!block) continue;
    const prefixLen = (block.dataset.prefix ?? "").length;
    // 本文は「マーカー span を除く子孫テキストノードの総和」。部分書式の span が入ると
    // 本文が複数ノードへ分かれるため、単一テキストノード前提では復元先を取り違える。
    const textNodes = blockTextNodes(block);
    // 本文長は分割されたテキストノードの長さの合計（部分書式 span で複数ノードへ分かれる）。
    const contentLen = textNodes.reduce((sum, node) => sum + (node.textContent?.length ?? 0), 0);
    const lineLen = prefixLen + contentLen;
    const isLast = i === blocks.length - 1;
    // この行の範囲（行頭 base 〜 行末 base+lineLen）に収まるか。最終行は超過分もこの行へ寄せる。
    if (offset <= base + lineLen || isLast) {
      const contentOffset = clamp(offset - base - prefixLen, 0, contentLen);
      const anchor = resolveContentAnchor(textNodes, contentOffset);
      // blank 行（<br> のみ）などテキストノードが無い場合はブロック先頭へ。
      return anchor ?? { node: block, offset: 0 };
    }
    base += lineLen + 1; // 改行 1 文字ぶん進める。
  }
  return null;
}

/**
 * 本文テキストノード列のうち、本文オフセット `contentOffset` に対応するノードと
 * ノード内オフセットを返す。境界は前のノード末尾ではなく次のノード先頭へ寄せず、
 * 走査順で最初に収まるノードを採る（部分書式 span の境界でキャレットが 1 文字ずれないようにする）。
 */
function resolveContentAnchor(
  textNodes: readonly Text[],
  contentOffset: number
): { node: Text; offset: number } | null {
  let remaining = contentOffset;
  let last: Text | null = null;
  for (const node of textNodes) {
    const length = node.textContent?.length ?? 0;
    if (remaining <= length) return { node, offset: remaining };
    remaining -= length;
    last = node;
  }
  return last === null ? null : { node: last, offset: last.textContent?.length ?? 0 };
}

/**
 * MarkdownTextView の表示テキスト（markdownLineTextFor）上での行内オフセット。
 * 非編集表示は本文を表示セグメント経由で描画するため、`[label](url)` は `label` へ縮約され、
 * インライン装飾のマーカー（`**` 等）は表示されない。本文内の表示オフセット
 * （displayOffsetInLine の結果）へ、箇条書きグリフ / 番号付きリストの項番の接頭辞長を足す。
 * `orderedLabel` は source map の採番結果（ordered 以外は undefined でよい）。
 */
function viewLineOffset(
  parts: MarkdownLineParts,
  orderedLabel: string | undefined,
  displayOffset: number
): number {
  if (parts.kind === "bullet" || parts.kind === "ordered") {
    // 非編集表示のマーカーは固定幅の span（`.canvas-md-list-marker`）で、中身は末尾の区切り空白を
    // 落としたグリフ / 項番だけ（`•` / `1-1.`）。マーカーと本文の間隔は span の幅が作るため、
    // 表示テキストノードの総和に空白は含まれない。接頭辞長もその実体（マーカー文字列の長さ）に
    // そろえる。番号付きリストの接頭辞は階層で変わる（`1.` / `1-1.` / 第 3 階層以降は `•`）ため、
    // 採番済みの接頭辞から求める。
    // 本文が空のリスト行でも表示にはマーカーが存在するため、本文位置は常にマーカーの後ろになる
    // （キャレットが `•` の左へ回り込まない）。
    const model = markdownTextLineFromParts(parts, { orderedLabel });
    return markdownListMarkerTextFor(model).length + displayOffset;
  }
  if (parts.kind === "rule") {
    // 水平線は非編集表示の表示テキストが空（DOM は <br> だけ）。content の `---` を通常どおり
    // 換算すると存在しない表示位置 1〜3 を返してキャレットが行外へ出るため、常に 0 にする。
    return 0;
  }
  // 引用は表示にマーカーが出ないため接頭辞長 0（見出し・plain と同じ）。
  return displayOffset;
}

/**
 * 表示モデルからブロックのスタイルを適用する（markdown-text.ts の markdownLineStyleFor を単一の真実として共有）。
 * 引用・水平線の見た目は CSS クラス（markdownLineClassName）で与え、インラインスタイルには書かない。
 * context には直前の行の種別を渡し、連続する箇条書きの項目間隔（marginTop）を非編集表示と一致させる。
 */
function applyLineStyle(
  div: HTMLElement,
  model: MarkdownTextLine,
  context: MarkdownLineStyleContext
): void {
  const style = markdownLineStyleFor(model, context);
  if (style.fontSize !== undefined) div.style.fontSize = style.fontSize;
  if (style.fontWeight !== undefined) div.style.fontWeight = style.fontWeight;
  if (style.marginTop !== undefined) div.style.marginTop = style.marginTop;
  if (style.paddingLeft !== undefined) div.style.paddingLeft = style.paddingLeft;
  if (style.textAlign !== undefined) div.style.textAlign = style.textAlign;
  if (style.textIndent !== undefined) div.style.textIndent = style.textIndent;
  const className = markdownLineClassName(model);
  if (className !== undefined) div.classList.add(className);
}

/**
 * Enter で次行へ複製する記法プレフィックス。継続しない種別（見出し・plain・blank・水平線）は null。
 * - 箇条書き: 生プレフィックスをそのまま（`*` / `+`・字下げを保存）。
 * - タスクリスト: タスクとして分類する前と同じ、字下げ + 箇条書きマーカー + 空白。
 *   チェックボックス自体の継続は挿入支援の責務なので、ここでは追加しない。
 * - 番号付きリスト: 字下げ + 「生テキストの数字 + 1」+ `. `。上限（ORDERED_NUMBER_MAX）では
 *   桁上がりさせず同じ数字にする（10 桁は記法として認識されず、継続の契約が壊れるため）。
 * - 引用: `> `（マーカーだけの `>` から継続する場合も本文を書ける `> ` にする）。
 */
function continuationPrefixFor(parts: MarkdownLineParts): string | null {
  switch (parts.kind) {
    case "bullet":
      return parts.rawPrefix;
    case "task": {
      const indent = parts.rawPrefix.slice(
        0,
        parts.rawPrefix.length - parts.rawPrefix.trimStart().length
      );
      const marker = parts.rawPrefix.trimStart().charAt(0) || "-";
      return `${indent}${marker} `;
    }
    case "ordered": {
      const number = markdownOrderedSourceNumber(parts) ?? 1;
      const next = number >= ORDERED_NUMBER_MAX ? number : number + 1;
      const indent = parts.rawPrefix.slice(
        0,
        parts.rawPrefix.length - parts.rawPrefix.trimStart().length
      );
      return `${indent}${next}. `;
    }
    case "quote":
      return "> ";
    default:
      return null;
  }
}

/**
 * 箇条書きのグリフ・番号付きリストの項番を描画する編集不可のマーカー span を作る。
 * 本文（テキストノード）とは区別され、serialize・raw offset 算出・キャレット復元の走査から除外される。
 *
 * マーカー領域の固定幅（`.canvas-md-list-marker` + `--marker-width`）は非編集表示と共有する。
 * 幅を与えないと、編集を始めた瞬間に本文の開始位置が非編集表示とずれる。
 * span へ入れる文字列は末尾の区切り空白を落としたもので、間隔はマーカー領域の幅が作る。
 */
function createMarkerSpan(doc: Document, line: MarkdownTextLine): HTMLElement {
  const marker = doc.createElement("span");
  const listMarkerClass = markdownListMarkerClassName(line);
  marker.className =
    listMarkerClass === undefined
      ? EDITOR_MARKER_CLASS
      : `${EDITOR_MARKER_CLASS} ${listMarkerClass}`;
  const markerStyle = markdownListMarkerStyleFor(line);
  if (markerStyle !== undefined) {
    marker.style.setProperty(
      MARKDOWN_LIST_MARKER_WIDTH_PROPERTY,
      markerStyle[MARKDOWN_LIST_MARKER_WIDTH_PROPERTY]
    );
  }
  marker.setAttribute("contenteditable", "false");
  marker.setAttribute("aria-hidden", "true");
  marker.textContent = markdownListMarkerTextFor(line);
  return marker;
}

/** 編集 DOM 用の非操作チェックボックス。マーカー扱いにして本文・serialize から除外する。 */
function createTaskBoxSpan(doc: Document, checked: boolean): HTMLElement {
  const box = doc.createElement("span");
  box.className = `${EDITOR_MARKER_CLASS} ${MARKDOWN_TASK_BOX_CLASS}`;
  box.dataset.checked = String(checked);
  box.setAttribute("contenteditable", "false");
  box.setAttribute("aria-hidden", "true");
  return box;
}

/** マーカー span（`.rc-md-marker`）そのものか。本文の走査から除外する対象。 */
function isMarkerElement(node: Node): boolean {
  return node instanceof HTMLElement && node.classList.contains(EDITOR_MARKER_CLASS);
}

/**
 * ブロックの本文テキストノードを文書順で返す。マーカー span の内側は本文ではないため除外し、
 * 部分書式 span（`.rc-md-run`）の内側は本文として含める。
 */
function blockTextNodes(block: HTMLElement): readonly Text[] {
  const nodes: Text[] = [];
  const collect = (parent: Node): void => {
    for (const node of Array.from(parent.childNodes)) {
      if (isMarkerElement(node)) continue;
      if (node.nodeType === Node.TEXT_NODE) {
        nodes.push(node as Text);
        continue;
      }
      if (node.nodeType === Node.ELEMENT_NODE) collect(node);
    }
  };
  collect(block);
  return nodes;
}

/** ブロックの本文（マーカー span を除く子孫テキストノードの連結）。<br> 等の要素は含めない。 */
function blockContentText(block: HTMLElement): string {
  let text = "";
  for (const node of blockTextNodes(block)) {
    text += node.textContent ?? "";
  }
  return text;
}

/**
 * serialize で信用してよい生プレフィックスを返す。
 * bullet・ordered ブロックは自前 build（buildMarkdownEditorDom）が必ず編集不可マーカー span を入れるため、
 * span を欠く bullet / ordered は「IME 変換中の Enter がネイティブ委任されたときのブロック分割クローン」
 * （data-* 属性は複製されるがキャレット前方のマーカー span は複製されない）とみなし、
 * data-prefix を信用せず plain 行として扱う。幻の空箇条書き・番号付き行が具現化するより、本文のみの
 * plain 行へ正準化（editorNeedsRebuild が span 欠落を直接検知して再構築）される方が安全。
 * 見出し・引用・水平線・plain はマーカー span を持たないのが正準のため、従来どおり data-prefix を用いる。
 */
function trustedBlockPrefix(node: HTMLElement): string {
  if (
    (node.dataset.md === "bullet" || node.dataset.md === "ordered" || node.dataset.md === "task") &&
    node.querySelector(`.${EDITOR_MARKER_CLASS}`) === null
  ) {
    return "";
  }
  return node.dataset.prefix ?? "";
}

/**
 * ノード + ノード内オフセットを、`el` 先頭からの生テキストオフセットへ変換する。
 * 各ブロックの生行長（prefix 長 + 本文長）を積算し、対象ブロックの prefix 長 + 本文内オフセットを足す。
 */
function rawOffsetOfPoint(el: HTMLElement, node: Node, nodeOffset: number): number | null {
  const blocks = Array.from(el.children).filter((c): c is HTMLElement => c instanceof HTMLElement);
  // 本文長は直下テキストノードの全連結で数える。IME 確定直後はブラウザが既存の空テキストノードの
  // 後ろへ確定テキストを別ノードとして挿入するため、firstTextNode だけでは本文長を取りこぼし、
  // キャレット位置（生オフセット）が本文先頭側へずれて Enter の分割位置等が壊れる。
  // prefix 長は serialize と同じ信頼済み値（trustedBlockPrefix）で数える。マーカー span を欠く
  // bullet の data-prefix をここで加算すると serialize が作る生テキストの座標系とずれ、
  // 再構築前のキャレット保存（reconcileAfterNativeInput）が後方へずれた位置を保存してしまう。
  const rawLenOf = (block: HTMLElement): number =>
    trustedBlockPrefix(block).length + blockContentText(block).length;
  // Range のコンテナが編集要素 root 自身の場合（Ctrl+A の全選択など）は、nodeOffset を子ブロックの
  // index として生オフセットへ写像する（index 0=先頭, index i=ブロック i の直前, 末尾=全長）。
  if (node === el) {
    if (nodeOffset <= 0) return 0;
    let acc = 0;
    for (let i = 0; i < blocks.length; i += 1) {
      if (i === nodeOffset) return acc;
      const block = blocks[i];
      if (block) acc += rawLenOf(block) + 1;
    }
    // 末尾（全ブロック選択の終端）。末尾に付いた余分な改行 1 文字ぶんを引いて全長へ。
    return Math.max(0, acc - 1);
  }
  let base = 0;
  for (const block of blocks) {
    const prefixLen = trustedBlockPrefix(block).length;
    const contentLen = blockContentText(block).length;
    if (block === node || block.contains(node)) {
      const contentOffset = contentOffsetWithinBlock(block, node, nodeOffset, contentLen);
      return base + prefixLen + contentOffset;
    }
    base += prefixLen + contentLen + 1; // 改行 1 文字ぶん。
  }
  return null;
}

/** ブロック内での本文オフセットを求める（本文テキストノード基準。要素境界は先頭/末尾へ寄せる）。 */
function contentOffsetWithinBlock(
  block: HTMLElement,
  node: Node,
  nodeOffset: number,
  contentLen: number
): number {
  // 本文は「マーカー span を除く子孫テキストノード」の連結。IME 確定直後は複数テキストノード
  // （空ノード + 確定ノード）に分かれ得るし、部分書式では span 内へ分かれるため、対象ノードより
  // 前にある本文テキストノードの長さを積算して本文オフセットへ換算する。
  // 正準な単一ノードブロックでは従来（先頭テキストノード基準の clamp）と完全一致する。
  if (node.nodeType === Node.TEXT_NODE) {
    let preceding = 0;
    for (const child of blockTextNodes(block)) {
      if (child === node) {
        return clamp(preceding + nodeOffset, 0, contentLen);
      }
      preceding += child.textContent?.length ?? 0;
    }
    return contentLen;
  }
  // node がブロック要素自身（子 index）や <br> の場合は、子 index から前後どちらへ寄せるか決める。
  if (node === block) {
    // 子が 0 個 / index 0 の位置は本文先頭、それ以外は本文末尾へ寄せる。
    return nodeOffset <= 0 ? 0 : contentLen;
  }
  // 部分書式 span 自身が Range のコンテナになった場合（span 境界のクリック等）は、その span より
  // 前の本文長 + 子 index から寄せ先を決める。
  if (node instanceof HTMLElement && node.classList.contains(EDITOR_RUN_CLASS)) {
    let preceding = 0;
    for (const child of blockTextNodes(block)) {
      if (node.contains(child)) {
        const spanLength = (node.textContent ?? "").length;
        return clamp(nodeOffset <= 0 ? preceding : preceding + spanLength, 0, contentLen);
      }
      preceding += child.textContent?.length ?? 0;
    }
    return contentLen;
  }
  // それ以外（マーカー span 内などの想定外ノード）は本文末尾へ寄せる。
  return contentLen;
}

/** offset を含む行の範囲と行文字列を返す（改行を含まない 1 行）。 */
function lineBoundsAt(
  text: string,
  offset: number
): { lineStart: number; lineEnd: number; lineText: string } {
  const clamped = clamp(offset, 0, text.length);
  const lineStart = text.lastIndexOf("\n", clamped - 1) + 1;
  const nextNewline = text.indexOf("\n", clamped);
  const lineEnd = nextNewline === -1 ? text.length : nextNewline;
  return { lineEnd, lineStart, lineText: text.slice(lineStart, lineEnd) };
}

/** start/end を [0,len] にクランプし昇順で返す。 */
function orderedBounds(text: string, start: number, end: number): [number, number] {
  const a = clamp(start, 0, text.length);
  const b = clamp(end, 0, text.length);
  return a <= b ? [a, b] : [b, a];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
